<template>
    <Teleport to="body">
        <div :class="positionClasses" class="pointer-events-none">
            <TransitionGroup
                :enter-active-class="enterActiveClass"
                :enter-from-class="enterFromClass"
                :enter-to-class="enterToClass"
                :leave-active-class="leaveActiveClass"
                :leave-from-class="leaveFromClass"
                :leave-to-class="leaveToClass"
            >
                <div
                    v-for="toast in visibleToasts"
                    :key="toast.id"
                    :class="toastClasses(toast.type)"
                    class="flex items-start w-full max-w-sm p-4 mb-3 rounded-lg shadow-lg backdrop-blur-sm pointer-events-auto transform transition-all duration-300 hover:shadow-xl"
                    role="alert"
                    :aria-live="toast.type === 'error' ? 'assertive' : 'polite'"
                    @mouseenter="pauseAutoClose(toast.id)"
                    @mouseleave="resumeAutoClose(toast.id)"
                >
                    <!-- Icon -->
                    <div class="flex-shrink-0" :class="iconClasses(toast.type)">
                        <!-- Success Icon -->
                        <svg v-if="toast.type === 'success'" class="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                        </svg>
                        <!-- Error Icon -->
                        <svg v-else-if="toast.type === 'error'" class="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                        </svg>
                        <!-- Warning Icon -->
                        <svg v-else-if="toast.type === 'warning'" class="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                        </svg>
                        <!-- Info Icon -->
                        <svg v-else class="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                        </svg>
                    </div>

                    <!-- Content -->
                    <div class="ml-3 flex-1 min-w-0">
                        <p v-if="toast.title" class="text-sm font-semibold mb-0.5">
                            {{ toast.title }}
                        </p>
                        <p class="text-sm break-words">
                            {{ toast.message }}
                        </p>

                        <!-- Action Button -->
                        <button
                            v-if="toast.action"
                            @click="handleAction(toast)"
                            class="mt-2 text-sm font-semibold underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-offset-1 rounded transition-colors"
                            :class="actionClasses(toast.type)"
                        >
                            {{ toast.action.label }}
                        </button>

                        <!-- Progress Bar (for auto-dismiss) -->
                        <div
                            v-if="toast.duration > 0 && showProgress"
                            class="mt-2 h-1 bg-white/20 dark:bg-black/20 rounded-full overflow-hidden"
                        >
                            <div
                                class="h-full transition-all ease-linear"
                                :class="progressClasses(toast.type)"
                                :style="{ width: getProgress(toast) + '%' }"
                            ></div>
                        </div>
                    </div>

                    <!-- Close Button -->
                    <button
                        @click="remove(toast.id)"
                        class="ml-3 flex-shrink-0 inline-flex rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-1 transition-colors p-1"
                        :class="closeButtonClasses(toast.type)"
                        :aria-label="'Close ' + toast.type + ' notification'"
                    >
                        <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                        </svg>
                    </button>
                </div>
            </TransitionGroup>
        </div>
    </Teleport>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useToast } from '@/Composables/useToast';

const props = defineProps({
    // Position of toast container
    position: {
        type: String,
        default: 'top-right',
        validator: (value) => [
            'top-right',
            'top-left',
            'bottom-right',
            'bottom-left',
            'top-center',
            'bottom-center',
        ].includes(value),
    },
    // Maximum number of toasts to show at once
    maxToasts: {
        type: Number,
        default: 5,
    },
    // Show progress bar
    showProgress: {
        type: Boolean,
        default: true,
    },
});

const { toasts, remove } = useToast();

// Track progress for each toast
const toastProgress = ref({});
const progressIntervals = ref({});
const pausedToasts = ref(new Set());

// Filter toasts to max limit
const visibleToasts = computed(() => {
    return toasts.value.slice(0, props.maxToasts);
});

// Position classes
const positionClasses = computed(() => {
    const baseClasses = 'fixed z-50 flex flex-col';
    const positions = {
        'top-right': 'top-4 right-4',
        'top-left': 'top-4 left-4',
        'bottom-right': 'bottom-4 right-4',
        'bottom-left': 'bottom-4 left-4',
        'top-center': 'top-4 left-1/2 -translate-x-1/2',
        'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
    };
    return `${baseClasses} ${positions[props.position] || positions['top-right']}`;
});

// Animation classes based on position
const enterActiveClass = computed(() => 'transition ease-out duration-300 transform');
const leaveActiveClass = computed(() => 'transition ease-in duration-200 transform');

const enterFromClass = computed(() => {
    if (props.position.includes('left')) {
        return 'translate-x-[-100%] opacity-0 scale-95';
    }
    if (props.position.includes('right')) {
        return 'translate-x-full opacity-0 scale-95';
    }
    return 'translate-y-[-100%] opacity-0 scale-95';
});

const enterToClass = computed(() => 'translate-x-0 translate-y-0 opacity-100 scale-100');

const leaveFromClass = computed(() => 'translate-x-0 translate-y-0 opacity-100 scale-100');

const leaveToClass = computed(() => {
    if (props.position.includes('left')) {
        return 'translate-x-[-100%] opacity-0 scale-95';
    }
    if (props.position.includes('right')) {
        return 'translate-x-full opacity-0 scale-95';
    }
    return 'translate-y-[-100%] opacity-0 scale-95';
});

// Toast styling based on type
const toastClasses = (type) => {
    const types = {
        success: 'bg-success-50 dark:bg-success-900/90 text-success-900 dark:text-success-50 border border-success-200 dark:border-success-700',
        error: 'bg-danger-50 dark:bg-danger-900/90 text-danger-900 dark:text-danger-50 border border-danger-200 dark:border-danger-700',
        warning: 'bg-warning-50 dark:bg-warning-900/90 text-warning-900 dark:text-warning-50 border border-warning-200 dark:border-warning-700',
        info: 'bg-info-50 dark:bg-info-900/90 text-info-900 dark:text-info-50 border border-info-200 dark:border-info-700',
    };
    return types[type] || types.info;
};

// Icon classes
const iconClasses = (type) => {
    const types = {
        success: 'text-success-500 dark:text-success-400',
        error: 'text-danger-500 dark:text-danger-400',
        warning: 'text-warning-500 dark:text-warning-400',
        info: 'text-info-500 dark:text-info-400',
    };
    return types[type] || types.info;
};

// Action button classes
const actionClasses = (type) => {
    const types = {
        success: 'text-success-700 dark:text-success-300 hover:text-success-800 dark:hover:text-success-200 focus:ring-success-500',
        error: 'text-danger-700 dark:text-danger-300 hover:text-danger-800 dark:hover:text-danger-200 focus:ring-danger-500',
        warning: 'text-warning-700 dark:text-warning-300 hover:text-warning-800 dark:hover:text-warning-200 focus:ring-warning-500',
        info: 'text-info-700 dark:text-info-300 hover:text-info-800 dark:hover:text-info-200 focus:ring-info-500',
    };
    return types[type] || types.info;
};

// Close button classes
const closeButtonClasses = (type) => {
    const types = {
        success: 'text-success-500 hover:text-success-700 dark:text-success-400 dark:hover:text-success-300 focus:ring-success-500',
        error: 'text-danger-500 hover:text-danger-700 dark:text-danger-400 dark:hover:text-danger-300 focus:ring-danger-500',
        warning: 'text-warning-500 hover:text-warning-700 dark:text-warning-400 dark:hover:text-warning-300 focus:ring-warning-500',
        info: 'text-info-500 hover:text-info-700 dark:text-info-400 dark:hover:text-info-300 focus:ring-info-500',
    };
    return types[type] || types.info;
};

// Progress bar classes
const progressClasses = (type) => {
    const types = {
        success: 'bg-success-500 dark:bg-success-400',
        error: 'bg-danger-500 dark:bg-danger-400',
        warning: 'bg-warning-500 dark:bg-warning-400',
        info: 'bg-info-500 dark:bg-info-400',
    };
    return types[type] || types.info;
};

// Handle action button click
const handleAction = (toast) => {
    if (toast.action && typeof toast.action.callback === 'function') {
        toast.action.callback();
    }
    // Remove toast after action
    remove(toast.id);
};

// Progress tracking
const getProgress = (toast) => {
    return toastProgress.value[toast.id] || 0;
};

const pauseAutoClose = (toastId) => {
    pausedToasts.value.add(toastId);
};

const resumeAutoClose = (toastId) => {
    pausedToasts.value.delete(toastId);
};

// Start progress tracking
const startProgressTracking = () => {
    visibleToasts.value.forEach(toast => {
        if (toast.duration > 0 && !progressIntervals.value[toast.id]) {
            toastProgress.value[toast.id] = 0;

            const updateInterval = 50; // Update every 50ms
            const increment = (updateInterval / toast.duration) * 100;

            progressIntervals.value[toast.id] = setInterval(() => {
                if (!pausedToasts.value.has(toast.id)) {
                    toastProgress.value[toast.id] += increment;

                    if (toastProgress.value[toast.id] >= 100) {
                        clearInterval(progressIntervals.value[toast.id]);
                        delete progressIntervals.value[toast.id];
                        delete toastProgress.value[toast.id];
                    }
                }
            }, updateInterval);
        }
    });
};

// Watch for new toasts
const stopProgressTracking = () => {
    Object.values(progressIntervals.value).forEach(interval => clearInterval(interval));
    progressIntervals.value = {};
    toastProgress.value = {};
};

// Watch for new toasts and start progress tracking
watch(() => visibleToasts.value, () => {
    if (props.showProgress) {
        startProgressTracking();
    }
}, { deep: true });

// Lifecycle
onMounted(() => {
    if (props.showProgress) {
        startProgressTracking();
    }
});

onUnmounted(() => {
    stopProgressTracking();
});
</script>

<style scoped>
/* Ensure smooth transitions */
* {
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

/* Custom scrollbar for long messages */
.break-words {
    word-wrap: break-word;
    overflow-wrap: break-word;
}
</style>
